<template>
  <div class="tool-page">
    <nav class="breadcrumb">
      <router-link to="/">{{ $t('nav.home') }}</router-link>
      <span class="separator">›</span>
      <span class="current">{{ $t('text2html.title') }}</span>
    </nav>

    <div class="tool-header">
      <h1>{{ $t('text2html.title') }}</h1>
      <p>{{ $t('text2html.subtitle') }}</p>
    </div>

    <div class="tool-card">
      <div class="options">
        <label class="option-label">
          <input type="radio" v-model="mode" value="paragraphs" name="mode" />
          {{ $t('text2html.modeParagraph') }}
        </label>
        <label class="option-label">
          <input type="radio" v-model="mode" value="list" name="mode" />
          {{ $t('text2html.modeList') }}
        </label>
        <label class="option-label">
          <input type="radio" v-model="mode" value="br" name="mode" />
          {{ $t('text2html.modeBr') }}
        </label>
        <label class="option-label">
          <input type="radio" v-model="mode" value="table" name="mode" />
          {{ $t('text2html.modeTable') }}
        </label>
      </div>

      <div class="actions">
        <button class="btn primary" @click="convert">{{ $t('common.convert') }}</button>
        <button class="btn" @click="copyResult" :disabled="!output">{{ $t('common.copyResult') }}</button>
        <button class="btn" @click="clearAll">{{ $t('common.clear') }}</button>
      </div>

      <div v-if="successMessage" class="alert success">{{ successMessage }}</div>

      <div class="editor-container">
        <div class="editor-panel">
          <label>{{ $t('text2html.inputLabel') }}</label>
          <textarea v-model="input" :placeholder="$t('text2html.inputPlaceholder')" spellcheck="false"></textarea>
        </div>
        <div class="editor-panel">
          <label>{{ $t('text2html.outputLabel') }}</label>
          <textarea v-model="output" readonly :placeholder="$t('text2html.outputPlaceholder')" spellcheck="false"></textarea>
        </div>
      </div>

      <div v-if="output" class="preview-section">
        <label>{{ $t('text2html.preview') }}</label>
        <div class="html-preview" v-html="output"></div>
      </div>
    </div>

    <div class="tool-description">
      <h2>{{ $t('text2html.whatIs') }}</h2>
      <p>{{ $t('text2html.whatIsDesc') }}</p>
      <h2>{{ $t('text2html.useCasesTitle') }}</h2>
      <ul>
        <li v-for="(item, i) in $t('text2html.useCases')" :key="i">{{ item }}</li>
      </ul>
      <h2>{{ $t('text2html.modesTitle') }}</h2>
      <ul>
        <li v-for="(item, i) in $t('text2html.modesDesc')" :key="i">{{ item }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Text2Html',
  data() {
    return { input: '', output: '', successMessage: '', mode: 'paragraphs' }
  },
  methods: {
    escapeHtml(text) {
      return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
    },
    convert() {
      if (!this.input.trim()) return
      const lines = this.input.split('\n').filter(l => l.trim() !== '')

      switch (this.mode) {
        case 'paragraphs':
          this.output = lines.map(l => `<p>${this.escapeHtml(l.trim())}</p>`).join('\n')
          break
        case 'list':
          this.output = '<ul>\n' + lines.map(l => `  <li>${this.escapeHtml(l.trim())}</li>`).join('\n') + '\n</ul>'
          break
        case 'br':
          this.output = lines.map(l => this.escapeHtml(l.trim())).join('<br>\n')
          break
        case 'table': {
          const rows = lines.map(l => l.split(/\t|,/).map(c => c.trim()))
          let html = '<table>\n'
          if (rows.length > 0) {
            html += '  <thead>\n    <tr>\n'
            rows[0].forEach(cell => { html += `      <th>${this.escapeHtml(cell)}</th>\n` })
            html += '    </tr>\n  </thead>\n'
          }
          if (rows.length > 1) {
            html += '  <tbody>\n'
            for (let i = 1; i < rows.length; i++) {
              html += '    <tr>\n'
              rows[i].forEach(cell => { html += `      <td>${this.escapeHtml(cell)}</td>\n` })
              html += '    </tr>\n'
            }
            html += '  </tbody>\n'
          }
          html += '</table>'
          this.output = html
          break
        }
      }
      this.successMessage = this.$t('text2html.success')
      setTimeout(() => { this.successMessage = '' }, 2000)
    },
    async copyResult() {
      if (!this.output) return
      try { await navigator.clipboard.writeText(this.output); this.successMessage = this.$t('common.copied') }
      catch { this.successMessage = this.$t('common.copyFailed') }
      setTimeout(() => { this.successMessage = '' }, 2000)
    },
    clearAll() { this.input = ''; this.output = ''; this.successMessage = '' }
  }
}
</script>

<style scoped>
.tool-page { max-width: 1200px; margin: 0 auto; }
.breadcrumb { margin-bottom: 24px; font-size: 14px; color: var(--text-secondary); }
.breadcrumb a { color: var(--accent-color); text-decoration: none; }
.breadcrumb a:hover { text-decoration: underline; }
.breadcrumb .separator { margin: 0 8px; color: var(--text-muted); }
.tool-header { text-align: center; margin-bottom: 32px; }
.tool-header h1 { font-size: 32px; margin-bottom: 12px; color: var(--text-primary); }
.tool-header p { color: var(--text-secondary); font-size: 16px; max-width: 600px; margin: 0 auto; line-height: 1.6; }
.tool-card { background: var(--bg-card); border: 1px solid var(--border-color); border-radius: 16px; padding: 24px; box-shadow: var(--shadow-md); }
.options { display: flex; gap: 20px; flex-wrap: wrap; margin-bottom: 16px; }
.option-label { display: flex; align-items: center; gap: 6px; color: var(--text-secondary); font-size: 14px; cursor: pointer; }
.actions { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 16px; }
.btn { background: var(--bg-tertiary); color: var(--text-primary); border: 1px solid var(--border-color); border-radius: 8px; padding: 10px 20px; cursor: pointer; font-size: 14px; transition: all 0.2s; }
.btn:hover { background: var(--bg-hover); }
.btn.primary { background: var(--accent-color); border-color: var(--accent-color); color: white; }
.btn.primary:hover { background: var(--accent-hover); }
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.alert.success { padding: 12px 16px; border-radius: 8px; margin-bottom: 16px; background: rgba(34,197,94,0.1); border: 1px solid rgba(34,197,94,0.3); color: var(--success-color); }
.editor-container { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
@media (max-width: 768px) { .editor-container { grid-template-columns: 1fr; } }
.editor-panel { display: flex; flex-direction: column; }
.editor-panel label { font-size: 14px; color: var(--text-secondary); margin-bottom: 8px; }
.editor-panel textarea { min-height: 300px; background: var(--bg-input); border: 1px solid var(--border-color); border-radius: 8px; padding: 16px; color: var(--text-primary); font-family: 'Monaco','Menlo','Ubuntu Mono',monospace; font-size: 13px; line-height: 1.5; resize: vertical; width: 100%; box-sizing: border-box; }
.editor-panel textarea:focus { outline: none; border-color: var(--accent-color); }
.preview-section { margin-top: 20px; }
.preview-section label { font-size: 14px; color: var(--text-secondary); display: block; margin-bottom: 8px; }
.html-preview { background: var(--bg-input); border: 1px solid var(--border-color); border-radius: 8px; padding: 16px; color: var(--text-primary); font-size: 14px; line-height: 1.8; min-height: 100px; }
.html-preview table { border-collapse: collapse; width: 100%; }
.html-preview th, .html-preview td { border: 1px solid var(--border-color); padding: 8px 12px; text-align: left; }
.html-preview th { background: var(--bg-tertiary); font-weight: 600; }
.tool-description { margin-top: 32px; background: var(--bg-card); border: 1px solid var(--border-color); border-radius: 16px; padding: 32px; box-shadow: var(--shadow-sm); }
.tool-description h2 { font-size: 20px; color: var(--text-primary); margin: 24px 0 16px; padding-bottom: 8px; border-bottom: 2px solid var(--accent-light); }
.tool-description h2:first-child { margin-top: 0; }
.tool-description p { font-size: 15px; color: var(--text-secondary); line-height: 1.8; margin-bottom: 16px; }
.tool-description ul { padding-left: 24px; margin-bottom: 16px; }
.tool-description li { font-size: 15px; color: var(--text-secondary); line-height: 1.8; margin-bottom: 8px; }
</style>
